import type { AccessWindow, EntitlementItem } from "./types"

/**
 * Matching a `libraryV2` card back to the commerce seat that licensed it.
 *
 * There are two ways to do it and they are not equal.
 *
 * **The LIN, when the card has one.** `LibraryItem.lins` and each asset's `lin`
 * are the Lifeway Item Numbers that granted the entry, and `itemNumber` on a
 * seat is the same number — myLifeway keys its own seat rows on it
 * (`/resources/{lin}/locations`). This is an identity, not a guess, and it is
 * what `resolve` uses whenever the card carries one. A card that carries a LIN
 * and matches nothing is reported as unmatched rather than falling back: the
 * card said which product it is, and no seat holds it.
 *
 * **The title, when it does not.** Until the `lins` field is deployed, every
 * card arrives without one, so the fallback below is what actually runs today.
 * It is a heuristic. It exists because it is the only thing that makes the
 * window visible on Studies / Digital Events / Ebooks at all, and it must never
 * gate access — only annotate it.
 *
 * Two rules keep the fallback from lying:
 *
 * 1. **Unique keys only.** A key that two seats claim with genuinely different
 *    windows is dropped rather than resolved arbitrarily. Ambiguity is invisible
 *    on a card; a wrong expiry date is not.
 * 2. **Never invent an absence.** No match means no window rendered — exactly
 *    what the section shows today — never "no expiry".
 */

/** Lowercase, strip punctuation, collapse whitespace. `"Amos: Video Sessions"` → `"amos video sessions"`. */
const normalize = (title: string): string =>
  title
    .toLowerCase()
    .replaceAll(/[^\p{L}\p{N}]+/gu, " ")
    .trim()

/**
 * Commerce names a *product*, the library names the *content*. The difference is
 * packaging boilerplate — the format, the edition, the licence — wrapped around
 * a study title the library carries bare.
 *
 * Stripping it is what an earlier `split(" - ")[0]` was reaching for and got
 * wrong: commerce writes ebooks as `"eBook - Daniel - Bible Study Book"`, whose
 * first segment is `"eBook"`, so all 32 ebook seats on the UAT account produced
 * the same stem, collided, and were dropped. Not one ebook seat was reachable.
 *
 * Anchored to the ends and never to the middle, so a qualifier that *identifies*
 * the product survives: `"Ephesians - Teen - Video Sessions"` stems to
 * `"ephesians teen"`, not to `"ephesians"`, and stays distinct from the adult
 * edition it used to cancel out.
 */
const PREFIXES = ["ebook"] as const

const SUFFIXES = [
  "updated",
  "bible study book",
  "bible study ebook",
  "bible study",
  "study guide",
  "video sessions",
  "video streaming redemption only",
  "video bundle streaming license",
  "streaming license",
  "digital access",
] as const

/**
 * Applied repeatedly, because commerce stacks them:
 * `"eBook - Jonah - Bible Study Book - Updated"` sheds three.
 */
const stripPackaging = (normalized: string): string => {
  let value = normalized
  let changed = true

  while (changed) {
    changed = false
    for (const prefix of PREFIXES) {
      if (value.startsWith(`${prefix} `)) {
        value = value.slice(prefix.length + 1)
        changed = true
      }
    }
    for (const suffix of SUFFIXES) {
      if (value.endsWith(` ${suffix}`)) {
        value = value.slice(0, -(suffix.length + 1))
        changed = true
      }
    }
  }

  return value.trim()
}

/**
 * The keys one title can be reached by: what it says, and what it says with the
 * packaging taken off. Both sides of the match run through this, so a bare
 * library title and a wrapped commerce title meet in the middle.
 */
export const keysFor = (title: string): readonly string[] => {
  const full = normalize(title)
  const stem = stripPackaging(full)

  return stem && stem !== full ? [full, stem] : [full]
}

const AMBIGUOUS = Symbol("ambiguous")

/** A library card, as much of it as matching needs. A bare string is a card with no LIN. */
export type CardRef = string | { readonly title: string; readonly lins?: readonly string[] }

const asCard = (card: CardRef): { title: string; lins: readonly string[] } =>
  typeof card === "string" ? { title: card, lins: [] } : { title: card.title, lins: card.lins ?? [] }

/**
 * Why a card has the window it has — or why it has none.
 *
 * `lookup` collapses all three failure shapes into `undefined`, which is right
 * for rendering (a card must never claim a window it cannot prove) and wrong for
 * the provenance overlay, whose entire job is to show where the match did not
 * reach.
 */
export type WindowLookup =
  | { readonly status: "matched"; readonly window: AccessWindow }
  | { readonly status: "unmatched" }
  | { readonly status: "ambiguous"; readonly keys: readonly string[] }
  /** No seats to match against at all — outside the POC provider, or the facade off. */
  | { readonly status: "inactive" }

export interface AccessWindowIndex {
  /** The seat window for a library card, or undefined when nothing matches unambiguously. */
  readonly lookup: (card: CardRef) => AccessWindow | undefined
  /** The same question, with the reason kept. For the overlay; rendering wants `lookup`. */
  readonly resolve: (card: CardRef) => WindowLookup
  /**
   * The item number the seat matching this title was licensed under, or
   * undefined when nothing matches it unambiguously.
   *
   * Title-keyed only: this answers the question a card asks when it has no LIN
   * of its own, which is every card until `Ebook.lin` reaches a deployed graph.
   */
  readonly linFor: (title: string) => string | undefined
  /** Seats available to match against — the rows `libraryV2` also serves. */
  readonly size: number
  /** Keys two different seats claimed with different windows, so neither can be used. */
  readonly ambiguousKeys: readonly string[]
}

export const EMPTY_WINDOW_INDEX: AccessWindowIndex = {
  lookup: () => undefined,
  resolve: () => ({ status: "inactive" }),
  linFor: () => undefined,
  size: 0,
  ambiguousKeys: [],
}

/**
 * Deliberately not `manageHref`. That link is derived from the licence id, so
 * two seats on one product — a normal condition, and seven of the 68 rows on the
 * UAT account — never compared equal, and every one of them read as two seats
 * disagreeing about a window they in fact agree on.
 */
const sameWindow = (a: AccessWindow, b: AccessWindow): boolean =>
  a.state === b.state && a.viewOn === b.viewOn && a.viewUntil === b.viewUntil && a.orderedOn === b.orderedOn

/**
 * Two seats that agree about the window are one answer. If they point at
 * different licences, the window survives and the manage link does not — the
 * card cannot say which of the two seats a Manage Access click should act on,
 * and sending it to the wrong licence is worse than not offering the control.
 */
const merge = (a: AccessWindow, b: AccessWindow): AccessWindow | typeof AMBIGUOUS => {
  if (!sameWindow(a, b)) {
    return AMBIGUOUS
  }

  return a.manageHref === b.manageHref ? a : { ...a, manageHref: undefined }
}

type Slot = AccessWindow | typeof AMBIGUOUS

const claim = (index: Map<string, Slot>, key: string, window: AccessWindow): void => {
  const existing = index.get(key)

  if (existing === undefined) {
    index.set(key, window)
  } else if (existing === AMBIGUOUS) {
    // Stays contested; a third seat cannot resolve what two already disagreed on.
  } else {
    index.set(key, merge(existing, window))
  }
}

/**
 * The same contest as `claim`, over item numbers rather than windows. Two seats
 * that name the same product under the same LIN are one answer; under different
 * LINs they are no answer, since the wrong one opens a different book.
 */
const claimLin = (index: Map<string, string | typeof AMBIGUOUS>, key: string, lin: string): void => {
  const existing = index.get(key)

  if (existing === undefined) {
    index.set(key, lin)
  } else if (existing !== AMBIGUOUS && existing !== lin) {
    index.set(key, AMBIGUOUS)
  }
}

export const buildAccessWindowIndex = (items: readonly EntitlementItem[]): AccessWindowIndex => {
  const byLin = new Map<string, Slot>()
  const byTitle = new Map<string, Slot>()
  const linByTitle = new Map<string, string | typeof AMBIGUOUS>()

  for (const item of items) {
    if (item.itemNumber) {
      claim(byLin, item.itemNumber, item.window)
    }
    for (const key of keysFor(item.title)) {
      claim(byTitle, key, item.window)

      if (item.itemNumber) {
        claimLin(linByTitle, key, item.itemNumber)
      }
    }
  }

  const contested = (index: Map<string, Slot>): readonly string[] =>
    [...index.entries()].filter(([, value]) => value === AMBIGUOUS).map(([key]) => key)

  const ambiguousKeys = [...contested(byLin), ...contested(byTitle)]

  /** First clean hit wins; otherwise report whichever keys were contested. */
  const against = (index: Map<string, Slot>, keys: readonly string[]): WindowLookup => {
    for (const key of keys) {
      const found = index.get(key)
      if (found !== undefined && found !== AMBIGUOUS) {
        return { status: "matched", window: found }
      }
    }

    const blocked = keys.filter(key => index.get(key) === AMBIGUOUS)

    return blocked.length > 0 ? { status: "ambiguous", keys: blocked } : { status: "unmatched" }
  }

  const resolve = (card: CardRef): WindowLookup => {
    if (items.length === 0) {
      return { status: "inactive" }
    }

    const { title, lins } = asCard(card)

    /**
     * No fallback to the title when the card carried a LIN. The card named the
     * product; if no seat holds that product there is nothing to find, and
     * reaching for the title from here would be guessing on top of an answer.
     */
    return lins.length > 0 ? against(byLin, lins) : against(byTitle, keysFor(title))
  }

  return {
    lookup: card => {
      const found = resolve(card)

      return found.status === "matched" ? found.window : undefined
    },
    resolve,
    linFor: title => {
      for (const key of keysFor(title)) {
        const found = linByTitle.get(key)

        if (found !== undefined && found !== AMBIGUOUS) {
          return found
        }
      }

      return undefined
    },
    size: items.length,
    ambiguousKeys,
  }
}
